import { SignalWatcher } from "@lit-labs/signals";
import { ContextProvider, consume, createContext } from "@lit/context";
import type { QueryClient } from "@tanstack/query-core";
import type { AnyRouteMatch, AnyRouter } from "@tanstack/router-core";
import { LitElement, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { RouteIntentEvent } from "./intents";

interface OutletContext {
    router: AnyRouter;
    /** Index in `router.stores.matches` of the match the providing outlet renders. */
    index: number;
}

const outletContext = createContext<OutletContext | undefined>(Symbol("ptc-route-outlet"));

/**
 * Renders the next matched route below its parent outlet that has a
 * `component`; layout routes render a nested `<ptc-route-outlet>` for their
 * children. Only the root outlet gets `.router`, nested ones find it through
 * context. Reading the router stores in render subscribes this element to
 * them (SignalWatcher).
 */
@customElement("ptc-route-outlet")
export class RouteOutlet extends SignalWatcher(LitElement) {
    @property({ attribute: false })
    router?: AnyRouter;

    @consume({ context: outletContext, subscribe: true })
    @state()
    private _parent?: OutletContext;

    private _provider = new ContextProvider(this, { context: outletContext });

    // Light DOM: the routed view must stay in the parent's shadow root to
    // pick up its layout styles (e.g. `.fullbleed` inside a section). No
    // `display: contents` either: a box-less clipping ancestor makes
    // @lit-labs/virtualizer render zero rows.
    protected createRenderRoot() {
        return this;
    }

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener(RouteIntentEvent.type, this._onIntent);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener(RouteIntentEvent.type, this._onIntent);
    }

    private get _router() {
        return this.router ?? this._parent?.router;
    }

    private _onIntent = (e: RouteIntentEvent) => {
        const router = this._router;
        if (!router) {
            return;
        }
        e.stopPropagation();
        const { intent } = e;
        switch (intent.type) {
            case "navigate":
                void router.navigate(intent.options);
                break;
            case "preload":
                router.preloadRoute(intent.options).catch(() => {});
                break;
            case "invalidate":
                void this._invalidate(router, intent.queryKey, intent.reload);
                break;
        }
    };

    private async _invalidate(router: AnyRouter, queryKey: readonly unknown[] | undefined, reload: boolean) {
        const { queryClient } = router.options.context as { queryClient?: QueryClient };
        if (queryKey) {
            await queryClient?.invalidateQueries({ queryKey, refetchType: "none" });
        }
        if (reload) {
            await router.invalidate();
        }
    }

    render() {
        const router = this._router;
        if (!router) {
            return nothing;
        }

        const from = this.router ? -1 : (this._parent?.index ?? -1);
        const matches = router.stores.matches.get();
        const index = matches.findIndex(
            (m: AnyRouteMatch, i: number) => i > from && router.routesById[m.routeId]?.options.component
        );
        const provided = this._provider.value;
        if (provided?.router !== router || provided.index !== index) {
            this._provider.setValue({ router, index });
        }
        if (index === -1) {
            return nothing;
        }

        const match = matches[index];
        const options = router.routesById[match.routeId].options;

        if (match.status === "error") {
            const errorComponent = options.errorComponent ?? router.options.defaultErrorComponent;
            return errorComponent
                ? errorComponent({ match, router, error: match.error })
                : html`<div class="padded">${String((match.error as Error)?.message ?? match.error)}</div>`;
        }

        if (match.status === "pending") {
            const pendingComponent = options.pendingComponent ?? router.options.defaultPendingComponent;
            return pendingComponent ? pendingComponent({ match, router }) : nothing;
        }

        return options.component!({ match, router });
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-route-outlet": RouteOutlet;
    }
}
